import { useEffect, useRef, useState } from 'react'
import { formatCents } from '@core/money'
import { groupHits, highlight, search, type Hit } from '@core/search'
import { Card, Spinner } from '@design/components'
import { DocumentViewer } from '@features/documents/DocumentViewer'
import { Overlay } from '@design/Overlay'

/**
 * Full-screen search.
 *
 * Grouped by type with counts, and every row shows the matched snippet with
 * the term highlighted — spec.md §20 is right that the snippet is what makes
 * OCR search feel intelligent rather than approximate. Seeing *why* something
 * matched is most of the value.
 *
 * Not a tab. It opens from the magnifier and closes back to where you were,
 * because searching is something you do in the middle of another task.
 */
export function SearchScreen({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<Hit[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [viewing, setViewing] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Debounced, because every keystroke is a database call and "metro" would
  // otherwise be five of them, four of which are already stale.
  useEffect(() => {
    if (query.trim().length < 2) {
      setHits(null)
      return
    }
    setBusy(true)
    const timer = setTimeout(() => {
      search(query)
        .then(setHits)
        .catch(() => setHits([]))
        .finally(() => setBusy(false))
    }, 250)
    return () => clearTimeout(timer)
  }, [query])

  const groups = hits ? groupHits(hits) : []

  return (
    <Overlay>
      <div className="shrink-0 pad-safe-top px-3 pb-2 flex items-center gap-2 border-b border-line">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek een factuur, brief of bedrag"
          inputMode="search"
          autoCorrect="off"
          autoCapitalize="none"
          className="flex-1 min-h-[3rem] px-4 rounded-xl bg-surface border border-line text-base"
        />
        <button onClick={onClose} className="text-muted px-2 py-2">
          Klaar
        </button>
      </div>

      <div className="flex-1 scroll-area px-4 py-3">
        {query.trim().length < 2 && (
          <p className="text-muted text-sm text-center py-10">
            Zoek op leverancier, bedrag, mededeling, of een woord dat op het
            document staat.
          </p>
        )}

        {busy && hits === null && <Spinner label="Zoeken…" />}

        {hits !== null && hits.length === 0 && !busy && (
          <p className="text-muted text-center py-10">Niets gevonden.</p>
        )}

        {groups.map((group) => (
          <section key={group.kind} className="mb-5">
            <h2 className="text-sm text-muted mb-2">
              {group.label} · {group.hits.length}
            </h2>
            {group.hits.map((hit) => (
              <HitRow
                key={`${hit.kind}-${hit.id}`}
                hit={hit}
                onOpen={() => hit.kind === 'document' && setViewing(hit.id)}
              />
            ))}
          </section>
        ))}
      </div>

      {viewing && <DocumentViewer documentId={viewing} onClose={() => setViewing(null)} />}
    </Overlay>
  )
}

function HitRow({ hit, onOpen }: { hit: Hit; onOpen: () => void }) {
  const segments = highlight(hit.snippet)

  return (
    <button onClick={onOpen} className="w-full text-left">
      <Card className="mb-2">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-medium truncate">{hit.title}</p>
          {hit.amountCents !== null && (
            <span className="tabular-nums shrink-0">{formatCents(hit.amountCents)}</span>
          )}
        </div>

        {segments.length > 0 && (
          <p className="text-sm text-muted mt-1 line-clamp-2">
            {segments.map((s, i) =>
              s.match ? (
                // Escaped by React, deliberately — ts_headline returns HTML and
                // OCR text comes off a page a stranger printed.
                <mark key={i} className="bg-warn/20 text-ink rounded px-0.5">
                  {s.text}
                </mark>
              ) : (
                <span key={i}>{s.text}</span>
              ),
            )}
          </p>
        )}

        {hit.occurredOn && (
          <p className="text-xs text-muted mt-1">
            {new Date(hit.occurredOn).toLocaleDateString('nl-BE', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </p>
        )}
      </Card>
    </button>
  )
}
